import { Attachment01, MessageChatCircle, Send01, XClose } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import csv from 'src/assets/img/FileTypeIcons/csv-lg.svg';
import defaultFileType from 'src/assets/img/FileTypeIcons/default-lg.svg';
import doc from 'src/assets/img/FileTypeIcons/doc-lg.svg';
import pdf from 'src/assets/img/FileTypeIcons/pdf-lg.svg';
import txt from 'src/assets/img/FileTypeIcons/txt-lg.svg';
import xls from 'src/assets/img/FileTypeIcons/xls-lg.svg';
import xlsx from 'src/assets/img/FileTypeIcons/xlsx-lg.svg';
import 'moment-timezone';
import AvatarGroup from 'src/components/AvatarGroup';
import ButtonCmp from 'src/components/ButtonCmp';
import InputTextArea from 'src/components/InputTextArea';
import NotFoundUI from 'src/components/NotFoundUI';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import {
    addSocketMessage as addOrderSocketMessage,
    catchUpOrderChatMessages,
    fetchOrderChatMembers,
    fetchOrderChatMessages,
    hideMessage as hideOrderMessage,
    incrementUnreadCount as incrementOrderUnreadCount,
    markOrderChatMessagesAsRead,
    resetOrderChat,
    sendOrderChatMessage,
    setIsSocketRoomJoined as setIsOrderSocketRoomJoined,
    updateMemberStatus as updateOrderMemberStatus,
} from 'src/redux/orderChat.slice';
import {
    addQuoteSocketMessage,
    catchUpQuoteChatMessages,
    fetchQuoteChatMembers,
    fetchQuoteChatMessages,
    hideQuoteMessage,
    incrementQuoteUnreadCount,
    markQuoteChatMessagesAsRead,
    resetQuoteChat,
    sendQuoteChatMessage,
    setIsQuoteSocketRoomJoined,
    updateQuoteMemberStatus,
} from 'src/redux/quoteChat.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import WalToast from 'src/utils/WalToast';

import MessageCard from './components/MessageCard';
import MessageCardSkeleton from './components/MessageCardSkeleton';
import { ALLOWED_FILE_TYPES, getFileExtension, getSocketRoomName, groupMessages, IEntityChatSalesRep, isCorruptFile, isDuplicateFile, MAX_FILE_SIZE, MAX_FILES } from './helpers';
import { IEntityChatMember, IEntityChatMessage } from './interface';

interface IChatViewProps {
    entityId: string | number;
    entityType: 'order' | 'quote';
    connectedUsers?: IEntityChatMember[];
    mainLoading?: boolean;
    isActive?: boolean;
    hideHeader?: boolean;
    isFromWidget?: boolean;
    chatContainerClassName?: string;
    autoFocusInput?: boolean;
    /** The entity's own sales rep (order/quote payload); falls back to currentUser.salesRep when absent. */
    entitySalesRep?: IEntityChatSalesRep | null;
}

const ChatView: FC<IChatViewProps> = ({
    entityId: entityIdProp,
    entityType,
    connectedUsers = [],
    mainLoading = false,
    isActive = false,
    hideHeader = false,
    isFromWidget = false,
    chatContainerClassName = '',
    autoFocusInput = true,
    entitySalesRep = null,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const isOrder = entityType === 'order';

    // Normalized to a string once, here, because `entityId` is this component's primary effect
    // dependency. Callers derive it from a mix of sources — a numeric id handed over in router
    // state for the first paint, the string route param afterwards — so the SAME entity used to
    // arrive as 11636 then "11636", which React sees as a changed dep: every fetch effect below
    // tore down and re-ran, duplicating the messages/members requests on each page open.
    const entityId = useMemo(() => (entityIdProp == null ? '' : String(entityIdProp)), [entityIdProp]);

    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const appearance = useSelector(appearanceSelector);
    const { socketIo, isSocketConnected } = useSelector((state: RootState) => state.basic);

    // Read from the appropriate slice based on entityType
    const orderChatState = useSelector((state: RootState) => state.orderChat);
    const quoteChatState = useSelector((state: RootState) => state.quoteChat);
    const { messages, members, isLoading, hasMoreMessages, lastMessageId, hiddenMessageIds, unreadCount } = isOrder ? orderChatState : quoteChatState;

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<any[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previousScrollHeightRef = useRef<number>(0);
    const isUserNearBottomRef = useRef<boolean>(true);
    const lastMessageIdRefForScroll = useRef<string | number | null>(null);
    const fileRef = useRef<HTMLParagraphElement>(null);
    const isPaginationLoadingRef = useRef(false);
    // False until this mount has joined the socket room once. Distinguishes the FIRST join (paired
    // with the mount fetch, nothing to catch up on) from a RE-join after the socket dropped.
    const hasJoinedRoomRef = useRef(false);
    // Mirrors currentUserRef: the socket effect below is not re-run when `isActive` changes, so the
    // handler would otherwise capture whatever isActive was when the room was joined.
    const isActiveRef = useRef(isActive);
    const currentUserRef = useRef(currentUser);
    useEffect(() => {
        currentUserRef.current = currentUser;
    }, [currentUser]);

    useEffect(() => {
        isActiveRef.current = isActive;
    }, [isActive]);

    // A different entity is a different thread: its first join must not be treated as a re-join.
    useEffect(() => {
        hasJoinedRoomRef.current = false;
    }, [entityId]);

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [filePreviews, setFilePreviews] = useState<Array<{ file: File; preview: string | null }>>([]);
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);

    const { handleSubmit, control, reset, watch } = useForm({});
    const messageWatcher = watch('message');

    const isLoadingCombined = isLoading || mainLoading;

    // The composer is a textarea that grows with its content (Teams/Slack style) instead of a
    // single-line input that scrolls sideways — long messages were unreadable while typing.
    // Growth is capped in CSS (max-h-*), at which point the textarea scrolls vertically.
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

    // Drives the composer's layout: a single line keeps the original compact row (bordered input
    // with the actions beside it), two or more lines switch to the Teams-style box (one border
    // round everything, actions on their own row at the bottom). Only CSS classes change between
    // the two — the element tree is identical, so the textarea is never remounted and focus and
    // caret position survive crossing the threshold mid-sentence.
    const [isComposerMultiline, setIsComposerMultiline] = useState(false);

    const autoSizeMessageInput = useCallback(() => {
        const textareaEl = messageInputRef.current;

        if (!textareaEl) return;

        // Reset to auto FIRST: scrollHeight never reports less than the current height, so without
        // this the box could only ever grow and would never shrink back as text is deleted.
        textareaEl.style.height = 'auto';

        // While the composer sits in an inactive tab or a collapsed widget it is display:none and
        // scrollHeight reads 0. Writing that back would pin the box shut at zero height, so leave
        // the natural rows={1} height in place; the isActive dep below re-measures once visible.
        if (!textareaEl.scrollHeight) return;

        // form_control is border-box, where `height` covers padding AND border, but scrollHeight
        // covers padding only. Without adding the borders back the box lands 2px short and shows a
        // permanent scrollbar on a single line of text.
        const borderHeight = textareaEl.offsetHeight - textareaEl.clientHeight;
        const contentHeight = textareaEl.scrollHeight;
        const desiredHeight = contentHeight + borderHeight;

        textareaEl.style.height = `${desiredHeight}px`;

        // Scroll ONLY once the max-height cap actually clamps the box. The height above is sized to
        // fit the content exactly, so any scrollbar below the cap is spurious — but scrollHeight,
        // offsetHeight and clientHeight are all integer-rounded, so a fractional line height can
        // leave the content a sub-pixel taller than the box. On Firefox that was enough to paint
        // custom-scrollbar-v2's grey thumb down the right edge of a single-line composer.
        const maxHeight = parseFloat(window.getComputedStyle(textareaEl).maxHeight) || Number.POSITIVE_INFINITY;
        textareaEl.style.overflowY = desiredHeight > maxHeight ? 'auto' : 'hidden';

        // Line count is derived from padding and line-height only — never from scrollHeight on its
        // own. scrollHeight is floored at clientHeight, and clientHeight changes with the border,
        // which MOVES between the textarea and its wrapper across these two layouts. Measuring it
        // that way latched the flag on: once the border came off, scrollHeight grew by the border
        // width and the composer could never return to its single-line form.
        // For the same reason the textarea's padding is identical in both layouts: if it changed,
        // flipping the flag would change the measurement and flip it straight back.
        const textareaStyle = window.getComputedStyle(textareaEl);
        const lineHeight = parseFloat(textareaStyle.lineHeight) || 16;
        const paddingY = parseFloat(textareaStyle.paddingTop) + parseFloat(textareaStyle.paddingBottom);
        const lineCount = Math.round((contentHeight - paddingY) / lineHeight);

        setIsComposerMultiline(lineCount > 1);
    }, []);

    // Runs on mount, on every keystroke, and when the tab/widget becomes visible (the first
    // measurement is skipped while hidden). Also collapses the box back to one row after send,
    // since handleSendMessage resets the field and drives messageWatcher back to ''.
    useLayoutEffect(() => {
        autoSizeMessageInput();
    }, [messageWatcher, isActive, autoSizeMessageInput]);

    const hiddenSet = useMemo(() => new Set(hiddenMessageIds.map(String)), [hiddenMessageIds]);

    const messagesList = useMemo(() => {
        const filtered = messages.filter((msg) => !hiddenSet.has(String(msg.id)));

        return groupMessages(filtered, appearance?.timezone);
    }, [messages, hiddenSet, appearance?.timezone]);

    // ----------------------------------------------------------------
    // Dispatch helpers — branch on entityType
    // ----------------------------------------------------------------
    const dispatchFetchMessages = useCallback(
        (opts: { lastId?: string | number | null }) => {
            if (isOrder) {
                return dispatch(fetchOrderChatMessages({ orderId: entityId, lastId: opts.lastId }));
            }

            return dispatch(fetchQuoteChatMessages({ quoteId: entityId, lastId: opts.lastId }));
        },
        [isOrder, entityId, dispatch]
    );

    const dispatchFetchMembers = useCallback(() => {
        if (isOrder) {
            return dispatch(fetchOrderChatMembers(entityId));
        }

        return dispatch(fetchQuoteChatMembers(entityId));
    }, [isOrder, entityId, dispatch]);

    const dispatchMarkAsRead = useCallback(() => {
        if (isOrder) {
            return dispatch(markOrderChatMessagesAsRead(entityId));
        }

        return dispatch(markQuoteChatMessagesAsRead(entityId));
    }, [isOrder, entityId, dispatch]);

    const dispatchReset = useCallback(() => {
        if (isOrder) {
            dispatch(resetOrderChat());
        } else {
            dispatch(resetQuoteChat());
        }
    }, [isOrder, dispatch]);

    const dispatchSetSocketRoomJoined = useCallback(
        (val: boolean) => {
            if (isOrder) {
                dispatch(setIsOrderSocketRoomJoined(val));
            } else {
                dispatch(setIsQuoteSocketRoomJoined(val));
            }
        },
        [isOrder, dispatch]
    );

    const dispatchAddSocketMessage = useCallback(
        (data: any) => {
            if (isOrder) {
                dispatch(addOrderSocketMessage(data));
            } else {
                dispatch(addQuoteSocketMessage(data));
            }
        },
        [isOrder, dispatch]
    );

    const dispatchIncrementUnreadCount = useCallback(() => {
        if (isOrder) {
            dispatch(incrementOrderUnreadCount());
        } else {
            dispatch(incrementQuoteUnreadCount());
        }
    }, [isOrder, dispatch]);

    const dispatchUpdateMemberStatus = useCallback(
        (payload: { memberId: string | number; status: string }) => {
            if (isOrder) {
                dispatch(updateOrderMemberStatus(payload));
            } else {
                dispatch(updateQuoteMemberStatus(payload));
            }
        },
        [isOrder, dispatch]
    );

    const dispatchHideMessage = useCallback(
        (id: string | number) => {
            if (isOrder) {
                dispatch(hideOrderMessage(id));
            } else {
                dispatch(hideQuoteMessage(id));
            }
        },
        [isOrder, dispatch]
    );

    // Re-fetches the latest page and merges it into Redux, deduplicated by id, so only genuinely
    // new messages are appended. Called ONLY when the socket room is re-joined after a drop (see
    // the join callback below) — messages sent while the socket was down arrive no other way.
    // The fetch itself lives in the slice so its in-flight guard also covers the second
    // ChatView instance on this page (tab + widget both join the room and would otherwise
    // each issue the same catch-up request).
    const catchUpOnMissedMessages = useCallback(() => {
        if (isOrder) {
            return dispatch(catchUpOrderChatMessages(entityId));
        }

        return dispatch(catchUpQuoteChatMessages(entityId));
    }, [isOrder, entityId, dispatch]);

    // ----------------------------------------------------------------
    // Initial fetch on mount
    // ----------------------------------------------------------------
    useEffect(() => {
        if (!entityId) return;

        dispatchFetchMessages({});
        dispatchFetchMembers();
        // Do NOT mark as read here — the isActive effect handles it only when the
        // user actually opens the Messages tab, so unread count stays visible on the badge.

        return () => {
            // The widget (isFromWidget=true) shares the same Redux slice as the main
            // chat tab. If the widget unmounts (e.g. user disables it in widget settings)
            // we must NOT reset the state — that would clear messages for the main tab too.
            // Only the main chat view is responsible for resetting shared state on unmount.
            if (!isFromWidget) {
                dispatchReset();
            }
        };
    }, [entityId]);

    // ----------------------------------------------------------------
    // Mark as read while the main view is on screen with unread messages
    // ----------------------------------------------------------------
    // Keyed on unreadCount, not just isActive, for two reasons: it skips the call entirely when
    // there is nothing to mark (switching to an already-read Messages tab used to POST every
    // time), and it still fires on the deep-link entry path (openMessagesTab), where the view
    // mounts already active and the unread count only becomes known once the fetch resolves.
    // Widgets are excluded — they are always rendered, so being on screen says nothing about
    // whether the customer read anything; they mark read through handleChatRead instead.
    // A backgrounded browser tab is not "seen", the same rule the socket handler applies.
    useEffect(() => {
        if (isFromWidget || !isActive || !entityId || unreadCount === 0) return;

        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

        dispatchMarkAsRead();
    }, [isFromWidget, isActive, entityId, unreadCount, dispatchMarkAsRead]);

    // ----------------------------------------------------------------
    // Scroll to bottom + focus input when tab becomes active
    // ----------------------------------------------------------------
    useEffect(() => {
        if (!isActive) return;

        setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }

            if (autoFocusInput) {
                messageInputRef.current?.focus();
            }
        }, 50);
    }, [isActive]);

    // ----------------------------------------------------------------
    // Socket room join/leave
    // ----------------------------------------------------------------
    useEffect(() => {
        if (!socketIo || !isSocketConnected || !entityId) return;

        const roomName = getSocketRoomName(entityId, entityType);

        socketIo.request({ method: 'POST', url: '/subscribe/joinRoom', data: { roomNames: [roomName] } }, () => {
            dispatchSetSocketRoomJoined(true);

            // Catch up ONLY on a re-join. The first join of a mount is paired with the initial
            // fetch dispatched moments earlier, so re-fetching the same page here bought nothing
            // and doubled the /messages request on every single page open. A re-join means the
            // socket actually dropped and came back, and messages genuinely can have been missed
            // while it was down — that is the case worth a request. addSocketMessage dedupes by
            // id, so anything already in state is skipped.
            // Trade-off, deliberate: a message sent in the window between the mount fetch and this
            // first join is no longer pulled in automatically; it arrives on the next open.
            if (hasJoinedRoomRef.current) {
                catchUpOnMissedMessages();
            }

            hasJoinedRoomRef.current = true;
        });

        const handleMessage = (data: any) => {
            dispatchAddSocketMessage(data);

            const currentUserId = currentUserRef.current?.id;
            const isFromCurrentUser = currentUserId && data.senderType === 'customer' && data.senderId?.toString() === currentUserId?.toString();

            // Only increment from the main chat view, not the widget.
            // The widget (isFromWidget=true) is always visible so the user is already
            // seeing incoming messages, and it registers its own socket listener which
            // would otherwise double-count every new message.
            if (!isFromCurrentUser && !isFromWidget) {
                // The chat is open AND the browser tab is in the foreground, so the customer is
                // actually looking at this message. Mark it read now rather than waiting for them
                // to click: lastReadAt is what drives the "Seen by" receipts WAL sees, and without
                // this a customer sitting on an open chat shows as never having seen anything.
                // A backgrounded tab does not count as seen, hence the visibility check.
                const isChatOnScreen = isActiveRef.current && typeof document !== 'undefined' && document.visibilityState === 'visible';

                if (isChatOnScreen) {
                    dispatchMarkAsRead();
                } else {
                    dispatchIncrementUnreadCount();
                }
            }
        };

        const handleStatusUpdate = (data: any) => {
            const memberId = data?.userId || data?.id || data?.customerId;
            const status = data?.status || data?.onlineStatus || data?.isOnline;

            if (memberId) {
                const resolvedStatus = status === true || status === 'online' || status === 'Online' ? 'online' : 'offline';
                dispatchUpdateMemberStatus({ memberId, status: resolvedStatus });
            }
        };

        socketIo.on('message', handleMessage);
        socketIo.on('userStatusUpdate', handleStatusUpdate);

        return () => {
            socketIo.off('message', handleMessage);
            socketIo.off('userStatusUpdate', handleStatusUpdate);
            dispatchSetSocketRoomJoined(false);
        };
    }, [socketIo, isSocketConnected, entityId, catchUpOnMissedMessages]);

    // ----------------------------------------------------------------
    // Scroll management
    // ----------------------------------------------------------------
    const isNearBottom = () => {
        if (!chatContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;

        return scrollHeight - scrollTop - clientHeight < 100;
    };

    useEffect(() => {
        const chatContainer = chatContainerRef.current;

        const handleScroll = () => {
            if (chatContainer) {
                isUserNearBottomRef.current = isNearBottom();
                previousScrollHeightRef.current = chatContainer.scrollHeight;
            }
        };

        chatContainer?.addEventListener('scroll', handleScroll);

        return () => chatContainer?.removeEventListener('scroll', handleScroll);
    }, []);

    useLayoutEffect(() => {
        if (!chatContainerRef.current || messagesList.length === 0) return;

        const lastMessage = messagesList[messagesList.length - 1];
        const currentLastMessageId = lastMessage?.id || null;
        const isNewMessage = currentLastMessageId && currentLastMessageId !== lastMessageIdRefForScroll.current;
        const container = chatContainerRef.current;

        if (!lastMessageId || messages.length <= 25) {
            container.scrollTop = container.scrollHeight;
            previousScrollHeightRef.current = container.scrollHeight;
            isUserNearBottomRef.current = true;
            lastMessageIdRefForScroll.current = currentLastMessageId;
        } else if (isNewMessage) {
            container.scrollTop = container.scrollHeight;
            previousScrollHeightRef.current = container.scrollHeight;
            isUserNearBottomRef.current = true;
            lastMessageIdRefForScroll.current = currentLastMessageId;
        }
    }, [messagesList, lastMessageId, messages.length]);

    // ----------------------------------------------------------------
    // Pagination — load more on scroll-to-top
    // ----------------------------------------------------------------
    const debouncedFetchMore = useCallback(
        debounce(() => {
            if (isPaginationLoadingRef.current || !hasMoreMessages || !lastMessageId) return;

            isPaginationLoadingRef.current = true;
            setIsPaginationLoading(true);
            const container = chatContainerRef.current;
            const previousScrollHeight = container?.scrollHeight || 0;

            dispatchFetchMessages({ lastId: lastMessageId }).finally(() => {
                isPaginationLoadingRef.current = false;
                setIsPaginationLoading(false);

                setTimeout(() => {
                    if (container) {
                        const newScrollHeight = container.scrollHeight;
                        container.scrollTop = newScrollHeight - previousScrollHeight;
                    }
                }, 50);
            });
        }, 300),
        [hasMoreMessages, lastMessageId, entityId]
    );

    useEffect(() => {
        const chatContainer = chatContainerRef.current;

        const handleWheel = () => {
            if (chatContainer && chatContainer.scrollTop === 0 && !isPaginationLoadingRef.current) {
                debouncedFetchMore();
            }
        };

        chatContainer?.addEventListener('wheel', handleWheel);

        return () => {
            chatContainer?.removeEventListener('wheel', handleWheel);
            debouncedFetchMore.cancel();
        };
    }, [debouncedFetchMore]);

    // ----------------------------------------------------------------
    // Mark as read on real interaction with the thread
    // ----------------------------------------------------------------
    // A real interaction with the thread — clicking anywhere in it, or focusing the composer —
    // is what counts as the customer having read it. This is the widget's only route to
    // mark-as-read (its isActive effect deliberately does not, see above), and a harmless no-op
    // for the main view, which has already marked read when its tab was opened.
    // Guarded on unreadCount so an idle click on an already-read thread sends nothing: without it
    // the autofocus the isActive effect triggers fired a second, identical mark-as-read on open.
    const handleChatRead = useCallback(() => {
        if (entityId && unreadCount > 0) {
            dispatchMarkAsRead();
        }
    }, [entityId, unreadCount, dispatchMarkAsRead]);

    // ----------------------------------------------------------------
    // Send message
    // ----------------------------------------------------------------
    const handleSendMessage = async (formData: any) => {
        const content = formData.message?.trim() || '';

        if (!content && selectedFiles.length === 0) return;

        const filesToSend = [...selectedFiles];
        reset({ message: '' });
        setSelectedFiles([]);
        setFilePreviews([]);

        let result;

        if (isOrder) {
            result = await dispatch(sendOrderChatMessage({ orderId: entityId, content, files: filesToSend.length > 0 ? filesToSend : undefined, currentUser }));
        } else {
            result = await dispatch(sendQuoteChatMessage({ quoteId: entityId, content, files: filesToSend.length > 0 ? filesToSend : undefined, currentUser }));
        }

        if (filesToSend.length > 0 && (result as any)?.type?.endsWith('/rejected')) {
            WalToast.error(t('Orders.Order_Stepper.File_Upload_Failed'));
        }

        setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
            messageInputRef.current?.focus();
        }, 100);
    };

    // ----------------------------------------------------------------
    // Resend failed message
    // ----------------------------------------------------------------
    const handleResendMessage = async (message: IEntityChatMessage) => {
        if (!message.hasError) return;

        const messageContent = message.content || '';
        const filesToResend = message._fileObjects || [];

        dispatchHideMessage(message.id);

        if (isOrder) {
            await dispatch(sendOrderChatMessage({ orderId: entityId, content: messageContent, files: filesToResend.length > 0 ? filesToResend : undefined, currentUser }));
        } else {
            await dispatch(sendQuoteChatMessage({ quoteId: entityId, content: messageContent, files: filesToResend.length > 0 ? filesToResend : undefined, currentUser }));
        }

        setTimeout(() => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
        }, 100);
    };

    // ----------------------------------------------------------------
    // File selection
    // ----------------------------------------------------------------
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const currentFileCount = selectedFiles.length;

        if (currentFileCount >= MAX_FILES) {
            WalToast.error(t('BookOrder.Booking_Stepper.Custom_Documents.Max_File_Limit_Reached_Description'));
            if (fileInputRef.current) fileInputRef.current.value = '';

            return;
        }

        const validFiles: File[] = [];
        const newPreviews: Array<{ file: File; preview: string | null }> = [];
        let filesAdded = 0;
        const remainingSlots = MAX_FILES - currentFileCount;
        let currentTotalSize = selectedFiles.reduce((total, file) => total + file.size, 0);

        for (const file of Array.from(files)) {
            if (filesAdded >= remainingSlots) break;

            if (isDuplicateFile(file, selectedFiles, validFiles)) {
                WalToast.error(`Duplicate File: ${file.name}`);
                continue;
            }

            if (file.size > MAX_FILE_SIZE) {
                WalToast.error(t('Order_Chat_File_Too_Large'));
                continue;
            }

            if (currentTotalSize + file.size > MAX_FILE_SIZE) {
                WalToast.error(t('Order_Chat_File_Too_Large'));
                continue;
            }

            if (!ALLOWED_FILE_TYPES.includes(file.type)) {
                WalToast.error(t('Order_Chat_Invalid_File_Type'));
                continue;
            }

            const corrupt = await isCorruptFile(file);

            if (corrupt) {
                WalToast.error(`${file.name} File corrupted`);
                continue;
            }

            validFiles.push(file);
            filesAdded++;
            currentTotalSize += file.size;

            if (file.type.startsWith('image/')) {
                const reader = new FileReader();

                reader.onloadend = () => {
                    setFilePreviews((prev) => [...prev, { file, preview: reader.result as string }]);
                };
                reader.readAsDataURL(file);
            } else {
                newPreviews.push({ file, preview: null });
            }
        }

        if (validFiles.length > 0) {
            setSelectedFiles((prev) => [...prev, ...validFiles]);
            setFilePreviews((prev) => [...prev, ...newPreviews]);
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (fileToRemove: File) => {
        setSelectedFiles((prev) => prev.filter((f) => f !== fileToRemove));
        setFilePreviews((prev) => prev.filter((p) => p.file !== fileToRemove));
    };

    const handleRemoveAllFiles = () => {
        setSelectedFiles([]);
        setFilePreviews([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ----------------------------------------------------------------
    // File icon helper (for preview area)
    // ----------------------------------------------------------------
    const getFileIconForPreview = (file: File) => {
        if (file.type.startsWith('image/')) return null;
        const extension = getFileExtension(file.name);
        const iconMap: Record<string, string> = { pdf, xls, xlsx, csv, txt, doc, docx: doc };

        return <img src={iconMap[extension] || defaultFileType} alt={extension.toUpperCase() || 'FILE'} className="w-10 h-10 object-contain" />;
    };

    const getFormattedDateTime = (date: any, convertTimezone = false) => {
        if (!date) return '';
        let momentDate = moment(date);
        if (!momentDate.isValid()) return '';

        if (convertTimezone && appearance?.timezone) {
            momentDate = moment.utc(date).tz(appearance.timezone);
        }

        const diffInSeconds = moment().diff(momentDate, 'seconds');

        if (diffInSeconds < 60) return 'just now';

        return momentDate.format('h:mm A');
    };

    // Merge store members with connectedUsers passed from parent
    const allMembers = useMemo(() => {
        const memberMap = new Map<string, IEntityChatMember>();
        [...members, ...connectedUsers].forEach((m) => {
            memberMap.set(String(m.id), m);
        });

        return Array.from(memberMap.values());
    }, [members, connectedUsers]);

    const memberAvatars = useMemo(() => {
        // The chat header is customer-facing: it shows the customer's own people and their sales rep,
        // never WAL staff. A WAL user browsing the portal via Microsoft SSO → customer selection
        // (isWalUserSession, synthesized by the portal backend) is excluded — as the pinned "current
        // user" avatar and as a message sender — across order, radar and quote chats.
        // NOTE: contacts flagged isAllowSuperAccess are real customer logins here and are NOT hidden
        // (hiding them removed genuine customer users from the header — 2026-09-16).
        const isWalStaffViewer = !!currentUser?.isWalUserSession;
        const walStaffSenderId = isWalStaffViewer && currentUser?.walUserId != null ? String(currentUser.walUserId) : null;

        // Build current user avatar — pinned first for customer contacts only
        const currentUserSrc = currentUser?.imageUrl && currentUser?.image ? `${currentUser.imageUrl}${currentUser.image}` : currentUser?.image || currentUser?.imageUrl || '';

        const currentUserAvatar =
            currentUser && !isWalStaffViewer
                ? {
                      id: String(currentUser.id),
                      src: currentUserSrc,
                      alt: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'You',
                      firstName: currentUser?.firstName || '',
                      user_status: 'online' as const,
                      isOnline: true,
                  }
                : null;

        // Build sales rep avatar — the entity's own rep first (order.salesRep / quote.user), then the
        // customer-level currentUser.salesRep. The latter is null for NEW customers or customers with
        // no active customer_sales_rep row, which previously left the header with no rep at all.
        const userSalesRep = (currentUser as any)?.salesRep;
        const salesRepName: string = entitySalesRep?.name || userSalesRep?.salesrepname || '';
        const salesRepImage: string = entitySalesRep?.name ? entitySalesRep.image : userSalesRep?.image || '';
        const salesRepAvatar = salesRepName
            ? {
                  id: 'salesrep',
                  src: salesRepImage,
                  alt: salesRepName,
                  firstName: salesRepName.split(' ')[0] || '',
                  user_status: 'offline' as const,
                  isOnline: false,
              }
            : null;

        // No messages yet — show only current user (customer contacts) + sales rep
        if (messages.length === 0) {
            return [currentUserAvatar, salesRepAvatar].filter(Boolean) as any[];
        }

        // Conversation started — derive unique senders from messages, pin current user first (customer contacts only)
        const senderMap = new Map<string, any>();

        if (currentUserAvatar) {
            senderMap.set(String(currentUser.id), currentUserAvatar);
        }

        // The sales rep is a fixed participant: keep them visible even before they have replied.
        if (salesRepAvatar) {
            senderMap.set(salesRepAvatar.id, salesRepAvatar);
        }

        messages.forEach((msg) => {
            const senderId = String(msg.userInfo?.id || '');
            if (!senderId || senderMap.has(senderId)) return;
            // WAL-side senders (senderType 'user') are never listed individually: the sales rep is
            // already the fixed avatar above, and any other WAL user replying from WALHQ is an admin
            // the customer must never see. Only customer contacts are derived from the messages.
            if (msg.senderType === 'user') return;
            const senderName = `${msg.userInfo?.firstName || ''} ${msg.userInfo?.lastName || ''}`.trim();
            // Skip the internal viewer's own messages so they never surface as a chat participant
            if (isWalStaffViewer && (senderId === String(currentUser.id) || (walStaffSenderId && senderId === walStaffSenderId))) return;

            senderMap.set(senderId, {
                id: senderId,
                // normalizeMessage already combines imageUrl+image into a full URL
                src: msg.userInfo?.image || '',
                alt: senderName || 'User',
                firstName: msg.userInfo?.firstName || '',
                user_status: 'offline',
                isOnline: false,
            });
        });

        return Array.from(senderMap.values());
    }, [messages, currentUser, allMembers, entitySalesRep]);

    const chatHeaderTitle = t('Common.Order_Chat_Header_Title', { defaultValue: 'Messages' });

    return (
        // onClick (not a button/key handler) is deliberate: this is not a control, it is a
        // read-receipt signal on the thread as a whole — clicking the message list, the composer
        // or the space around them all mean the customer looked at it. Keyboard users reach the
        // same path by tabbing into the composer, which fires handleChatRead via onFocus.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
            onClick={handleChatRead}
            className={`${isFromWidget ? 'flex flex-col flex-1' : 'flex flex-col flex-1 overflow-hidden'} ${!hideHeader ? 'rounded-xl border border-utilityGray200 shadow-md bg-white' : ''}`}
        >
            {/* Chat header — team members */}
            {!hideHeader && (
                <div className="flex items-center justify-between px-4 py-4 border-b border-utilityGray200 shrink-0">
                    <p className="text-sm font-semibold text-grayLight900">{chatHeaderTitle}</p>
                    {memberAvatars.length > 0 && <AvatarGroup avatars={memberAvatars} maxVisible={4} />}
                </div>
            )}

            {/* Message list */}
            <div
                className={`flex-1 flex flex-col overflow-auto custom-scrollbar-v2 p-4 ${hideHeader && messagesList.length === 0 && 'justify-center items-center'} ${chatContainerClassName}`}
                ref={chatContainerRef}
            >
                <ul className={`w-full ${messagesList.length === 0 && !isLoadingCombined ? 'h-full' : 'mt-auto'}`}>
                    {isPaginationLoading && messagesList.length > 0 && (
                        <li className="flex justify-center py-3">
                            <div className="flex items-center gap-2 text-grayLight600 text-sm">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                            </div>
                        </li>
                    )}

                    {isLoadingCombined && messagesList.length === 0 && <MessageCardSkeleton />}

                    {messagesList.length > 0 &&
                        messagesList.map((message: IEntityChatMessage, index: number) => (
                            <MessageCard
                                key={message.id || index}
                                message={message}
                                currentUser={currentUser}
                                getFormattedDateTime={getFormattedDateTime}
                                messageRefs={messageRefs}
                                chatMembers={allMembers}
                                onResend={handleResendMessage}
                                mainIndex={index}
                                isFromWidget={isFromWidget}
                            />
                        ))}

                    {messagesList.length === 0 && !isLoadingCombined && (
                        <li className="h-full flex flex-col">
                            <NotFoundUI
                                containerClassName="!my-0"
                                isEmptyBackground={true}
                                titleClassName="!px-0"
                                descClass="!px-0 !max-w-[420px] sm:!max-w-[500px]"
                                iconClassName="!hidden"
                                iconSecondaryClassName={`${isFromWidget ? '!hidden' : '!flex '}`}
                                iconSecondary={<MessageChatCircle className="w-6 h-6 text-primary" />}
                                title={t('Order_Chat_No_Messages')}
                                desc={t('Order_Chat_No_Messages_Desc')}
                            />
                        </li>
                    )}
                </ul>
            </div>

            {/* Input area */}
            <form onSubmit={handleSubmit(handleSendMessage)}>
                <div className={` ${isFromWidget ? 'xl:!px-3 xl:!py-2 p-3' : 'xl:p-4 p-3'} border-t border-utilityGray200`}>
                    {selectedFiles.length > 0 && (
                        <div className="mb-3 space-y-1.5">
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-grayLight900 font-normal">{`${selectedFiles.length} file${selectedFiles.length > 1 ? '(s)' : ''} selected`}</p>
                                <ButtonCmp type="button" size="none" onClick={handleRemoveAllFiles} className="btn-link-outline-primary !text-danger700">
                                    {selectedFiles.length > 1 ? 'Remove All' : 'Remove'}
                                </ButtonCmp>
                            </div>
                            <ul className="grid grid-cols-3 gap-2 items-end max-h-48 overflow-y-auto custom-scrollbar-v2">
                                {filePreviews.map((previewItem, index) => {
                                    const { file, preview } = previewItem;

                                    return (
                                        <li key={index} className={`${isFromWidget ? 'p-1.5' : 'px-3.5 py-2'} bg-utilityGray100 rounded-lg relative`}>
                                            <div className="gap-2.5 flex items-center">
                                                {preview ? (
                                                    <img src={preview} alt={file.name} className="w-10 h-10 object-cover rounded flex-none" />
                                                ) : (
                                                    <span className="flex-none">{getFileIconForPreview(file)}</span>
                                                )}
                                                <TruncateTextCmp message={file.name} refName={fileRef}>
                                                    <p className="text-xs text-grayLight900 font-normal truncate pr-4" ref={fileRef}>
                                                        {file.name}
                                                    </p>
                                                </TruncateTextCmp>
                                                <div className={`${isFromWidget ? 'top-1.5 right-1.5' : 'top-2.5 right-2.5'} absolute`}>
                                                    <TooltipCmp message="Remove file">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveFile(file)}
                                                            className={`${
                                                                isFromWidget ? 'w-4 h-4' : 'w-[22px] h-[22px]'
                                                            } bg-white rounded-full shadow-lgc text-grayLight600 hover:text-danger500 flex items-center justify-center`}
                                                        >
                                                            <XClose className="w-[13px] h-[13px]" />
                                                        </button>
                                                    </TooltipCmp>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Single line -> compact row, actions beside the input (the original look).
                        Multi-line -> the border moves to this wrapper, the textarea goes borderless
                        and full width, and the actions drop to their own bottom row inside the box.
                        focus-within reproduces the focus shadow form_control draws on the input. */}
                    <div className={isComposerMultiline ? 'flex flex-col overflow-hidden rounded-lg border border-borderPrimary bg-white focus-within:shadow' : 'flex items-end gap-2'}>
                        <Controller
                            name="message"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <InputTextArea
                                    inputName="message"
                                    placeholder={t('Order_Chat_Input_Placeholder')}
                                    value={value}
                                    textareaRef={messageInputRef}
                                    rows={1}
                                    className={`${isFromWidget ? 'max-h-[72px]' : 'max-h-[120px]'} overflow-hidden ${
                                        isComposerMultiline ? '!border-0 !rounded-none !shadow-none focus:!shadow-none !bg-transparent' : ''
                                    }`}
                                    onChangeFunc={(e) => {
                                        const inputValue = e?.target?.value || '';
                                        const trimmedValue = inputValue?.trim();

                                        if (trimmedValue === '' && inputValue?.length > 0) {
                                            onChange('');
                                        } else {
                                            onChange(inputValue?.trimStart());
                                        }
                                    }}
                                    handleKeyDown={(e: React.KeyboardEvent) => {
                                        // A textarea does not submit a form on Enter the way the old
                                        // <input> did, so sending is wired up explicitly here.
                                        // Shift+Enter inserts a newline. isComposing guards IME input
                                        // (e.g. Japanese/Chinese), where Enter commits the candidate
                                        // and must not send the message.
                                        if (e.key !== 'Enter' || e.shiftKey || (e.nativeEvent as any)?.isComposing) return;

                                        e.preventDefault();

                                        if (isLoadingCombined) return;

                                        handleSubmit(handleSendMessage)();
                                    }}
                                    onFocus={handleChatRead}
                                    parentClassName={isComposerMultiline ? 'w-full' : 'flex-1 min-w-0'}
                                    maxLength={10000}
                                />
                            )}
                        />
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple />

                        <div className={`flex items-center gap-2 ${isComposerMultiline ? 'justify-end px-2 pb-2' : ''}`}>
                            <TooltipCmp message={t('Order_Chat_Attach')}>
                                <ButtonCmp
                                    type="button"
                                    className="btn_secondary_black"
                                    size="square-md"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isLoadingCombined || selectedFiles.length >= MAX_FILES}
                                >
                                    <Attachment01 className="w-4 h-4" />
                                </ButtonCmp>
                            </TooltipCmp>
                            <TooltipCmp message={t('Order_Chat_Send')}>
                                <ButtonCmp className="btn_secondary_black" size="square-md" disabled={(!messageWatcher && selectedFiles.length === 0) || isLoadingCombined}>
                                    <Send01 className="w-4 h-4" />
                                </ButtonCmp>
                            </TooltipCmp>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ChatView;
